import type { CampaignSchema } from "@/types";
import type { Tournament } from "@/campaigns/modules/tournament/types";

import { currentCampaignEqual, resolveCurrentCampaign } from "@/campaigns";
import { useAppStore } from "@/stores";
import { useStoreWithEqualityFn } from "zustand/traditional";

/**
 * The selected (or explicitly addressed) campaign, resolved type-agnostically
 * through the campaign registry — a prize drop resolves here just like a
 * tournament (the breadcrumb, terms, description all read `campaignData`).
 * `tournamentData` stays tournament-only for the tournament-specific views.
 */
export const useCampaign = (idOrCustomId?: string | number) => {
  const selectedId = useAppStore((state) => state.selectedCampaignId);
  const resolvedId =
    idOrCustomId != null ? String(idOrCustomId) : (selectedId ?? "");

  const current = useStoreWithEqualityFn(
    useAppStore,
    (state) => (resolvedId ? resolveCurrentCampaign(state, resolvedId) : null),
    currentCampaignEqual
  );

  const campaignData: CampaignSchema | null = current?.campaign ?? null;
  const tournamentData: Tournament | null =
    current?.type === "tournament" ? (current.data as Tournament) : null;

  return {
    campaignData,
    tournamentData,
    // Join state is type-agnostic (`joinedById`, fed by `campaign_progress`).
    joined: current?.joined ?? false,
  };
};
